import { Level } from "../Level.js";
import { GRID_W, GRID_H, BASE_ROOMS, GAME } from "../../config/constants.js";
import { rndi, pick } from "../../core/util.js";
import { finalize } from "./helpers.js";

// Locked-chest tiers → model shape, its lid node (hinged open by the Game), and
// the number of Vault Keys it costs to open. Universal keys, tiered cost.
const LOCK_TIERS = {
  bronze: { shape: "chest",       lid: "chest_lid",       keyCost: 1 },
  silver: { shape: "chest_large", lid: "chest_large_lid", keyCost: 2 },
  gold:   { shape: "chest_gold",  lid: "chest_gold_lid",  keyCost: 3 },
};

/**
 * Single-level rooms-and-corridors dungeon.
 *
 * Every room is assigned an ARCHETYPE (storage, armory, hall, quarters, shrine,
 * treasure) that dictates its decor, so a storeroom always reads as a
 * storeroom instead of a random pile of props. Portcullis doors gate the room
 * entrances (open with V).
 */

const ARCHETYPES = ["storage", "armory", "hall", "quarters", "shrine", "treasure"];

function roomCells(r) {
  const perim = [], interior = [];
  for (let y = r.y + 1; y < r.y + r.h - 1; y++)
    for (let x = r.x + 1; x < r.x + r.w - 1; x++) {
      const edge = x === r.x + 1 || x === r.x + r.w - 2 || y === r.y + 1 || y === r.y + r.h - 2;
      (edge ? perim : interior).push([x, y]);
    }
  return { perim, interior };
}

function furnishRoom(level, theme, r, avoid) {
  const { perim, interior } = roomCells(r);
  const place = (gx, gy, shape, opts = {}) => {
    if (avoid.some(([ax, ay]) => Math.hypot(gx - ax, gy - ay) < 2.4)) return;
    level.addProp({
      gx: gx + 0.5, gy: gy + 0.5, shape,
      color: opts.color || 0x8a6a3a,
      blocking: opts.blocking !== false,
      breakable: !!opts.breakable, table: opts.table,
      scale: opts.scale || 1, rot: opts.rot != null ? opts.rot : Math.random() * Math.PI * 2,
      key: theme.assets && theme.assets[shape] ? theme.assets[shape] : null,
    });
  };
  const chance = (p) => Math.random() < p;

  // A tiered LOCKED chest: blocking, NOT breakable (a key opens it, not a sword).
  // Carries the tier + key cost + lid node so the builder/Game can hinge it open.
  const placeLocked = (gx, gy, tier) => {
    if (!GAME.lockedVaults) return false;
    if (avoid.some(([ax, ay]) => Math.hypot(gx - ax, gy - ay) < 2.4)) return false;
    const t = LOCK_TIERS[tier];
    // ~18% of bronze/silver locked chests are MIMICS — they spring into a monster
    // on open instead of paying out. Gold chests are never mimics (a 3-key trap
    // would feel unfair). Looks identical until opened.
    const mimic = tier !== "gold" && Math.random() < 0.18;
    level.addProp({
      gx: gx + 0.5, gy: gy + 0.5, shape: t.shape,
      color: 0xc9a24b, blocking: true, breakable: false,
      locked: true, tier, keyCost: t.keyCost, lidNode: t.lid, lootTier: tier, mimic,
      scale: 1, rot: Math.random() * Math.PI * 2,
      key: theme.assets && theme.assets[t.shape] ? theme.assets[t.shape] : null,
    });
    return true;
  };

  switch (r.kind) {
    case "storage": {
      for (const [x, y] of perim) {
        if (!chance(0.55)) continue;
        const s = pick(["barrel", "crate", "barrel_stack"]);
        place(x, y, s, { breakable: true, table: s === "crate" ? "crate" : "barrel", scale: 0.9 + Math.random() * 0.3 });
      }
      for (const [x, y] of interior) if (chance(0.12)) place(x, y, "bottle", { blocking: false, breakable: true, table: "pot" });
      if (chance(0.35)) { const c = interior[(interior.length / 2) | 0]; if (c) placeLocked(c[0], c[1], "bronze"); }
      break;
    }
    case "armory": {
      const topY = r.y + 1;
      for (let x = r.x + 2; x < r.x + r.w - 2; x += 2) if (chance(0.6)) place(x, topY, "bookcase", { blocking: true, rot: 0 });
      for (const [x, y] of perim) {
        if (y === topY) continue;   // leave the bookcase wall clear
        if (!chance(0.4)) continue;
        place(x, y, chance(0.5) ? "table" : "crate", { breakable: true, table: "crate" });
      }
      const c = interior[(interior.length / 2) | 0];
      if (c) { if (!(chance(0.5) && placeLocked(c[0], c[1], "silver"))) place(c[0], c[1], "chest", { table: "chest", breakable: true }); }
      break;
    }
    case "hall": {
      for (const [x, y] of perim) {
        const onLongRow = y === r.y + 2 || y === r.y + r.h - 3;
        if (onLongRow && (x - (r.x + 2)) % 2 === 0) place(x, y, "pillar", { scale: 1.0 });
      }
      const far = [r.x + r.w - 3, r.y + ((r.h / 2) | 0)];
      if (!(chance(0.45) && placeLocked(far[0], far[1], "silver"))) place(far[0], far[1], "chest", { table: "chest", breakable: true });
      break;
    }
    case "quarters": {
      const topRow = r.y + 1;
      for (let x = r.x + 2; x < r.x + r.w - 2; x += 2) place(x, topRow, "bed", { scale: 1.0, rot: 0 });
      for (const [x, y] of interior) {
        if (chance(0.10)) place(x, y, "table", { breakable: true, table: "crate" });
        else if (chance(0.14)) place(x, y, "candle", { blocking: false });
      }
      if (chance(0.3)) { const c = interior[((interior.length / 3) | 0)]; if (c) placeLocked(c[0], c[1], "bronze"); }
      break;
    }
    case "shrine": {
      for (const [x, y] of perim) if (chance(0.35)) place(x, y, "candle", { blocking: false });
      const c = interior[(interior.length / 2) | 0];
      if (c) { if (!(chance(0.4) && placeLocked(c[0], c[1], "bronze"))) place(c[0], c[1], "chest", { table: "chest", breakable: true }); }
      for (const [x, y] of interior) if (chance(0.08)) place(x, y, "bottle", { blocking: false, breakable: true, table: "pot" });
      break;
    }
    case "treasure": {
      const cx = r.x + ((r.w / 2) | 0), cy = r.y + ((r.h / 2) | 0);
      // the centerpiece is a GOLD locked chest (key-gated); falls back to a free
      // gold chest only when locked vaults are disabled
      if (!placeLocked(cx, cy, "gold")) place(cx, cy, "chest_gold", { table: "chest", breakable: true, scale: 1.15 });
      place(cx - 1, cy, "coins", { blocking: false, scale: 1.2 });
      place(cx + 1, cy, "coins", { blocking: false, scale: 1.2 });
      for (const [x, y] of perim) if (chance(0.3)) place(x, y, pick(["barrel", "crate"]), { breakable: true, table: "barrel" });
      break;
    }
  }
}

/**
 * Carve a SEALED vault room off to the side of the map (~1 in 3 floors, depth 2+).
 * It connects to the dungeon through a single choke gated by a LOCKED door
 * (3 keys). Its interior is unreachable until you pay — so `finalize` never puts
 * the exit or enemy spawns inside it. Opening the door pays out a guaranteed
 * hoard (see level.vaultReward), and the room is stocked with free chests: a
 * loot room is always worth more than any single locked chest.
 */
function carveVault(level, rooms, floor, theme) {
  if (!GAME.lockedVaults || floor < 2 || Math.random() > 0.35 || !rooms.length) return;
  const VW = 6, VH = 6;

  // find a footprint whose area + 1-cell margin is completely floor-free
  let spot = null;
  for (let tries = 0; tries < 240 && !spot; tries++) {
    const vx = rndi(2, GRID_W - VW - 2), vy = rndi(2, GRID_H - VH - 2);
    let clear = true;
    for (let y = vy - 1; y <= vy + VH && clear; y++)
      for (let x = vx - 1; x <= vx + VW && clear; x++)
        if (level.hasFloorGrid(x, y)) clear = false;
    if (clear) spot = { vx, vy };
  }
  if (!spot) return;
  const { vx, vy } = spot;

  for (let y = vy + 1; y < vy + VH - 1; y++)
    for (let x = vx + 1; x < vx + VW - 1; x++) level.setFloor(x, y, (x + y) % 2);
  const vcx = vx + (VW >> 1), vcy = vy + (VH >> 1);

  // nearest room center = where the entrance corridor connects
  let target = rooms[0], bd = Infinity;
  for (const r of rooms) { const d = Math.hypot(r.cx - vcx, r.cy - vcy); if (d < bd) { bd = d; target = r; } }

  // door sits on the vault wall on the side facing the target; corridor runs out
  const horiz = Math.abs(target.cx - vcx) >= Math.abs(target.cy - vcy);
  const dx = Math.sign(target.cx - vcx) || 1, dy = Math.sign(target.cy - vcy) || 1;
  const door = horiz
    ? { x: (dx > 0 ? vx + VW - 1 : vx), y: vcy }
    : { x: vcx, y: (dy > 0 ? vy + VH - 1 : vy) };
  level.setFloor(door.x, door.y);

  // L-corridor from just outside the door to the target centre, stopping the
  // moment we touch existing dungeon floor (guarantees a single connection)
  let x = door.x + (horiz ? dx : 0), y = door.y + (horiz ? 0 : dy);
  for (let guard = 0; guard < 140; guard++) {
    if (level.hasFloorGrid(x, y)) break;   // reached the dungeon
    level.setFloor(x, y);
    if (x !== target.cx) x += Math.sign(target.cx - x);
    else if (y !== target.cy) y += Math.sign(target.cy - y);
    else break;
  }

  // seal the choke with a locked (gold, 3-key) vault door
  if (level.block[door.y]) level.block[door.y][door.x] = 1;
  const ey = level.elevY[door.y] ? level.elevY[door.y][door.x] : 0;
  level.doors.push({
    cells: [[door.x, door.y]], wx: door.x, wz: door.y, rot: horiz ? Math.PI / 2 : 0, elevY: ey,
    locked: true, keyCost: 3, tier: "gold", vault: true,
  });
  level.vaultReward = { gx: vcx + 0.5, gy: vcy + 0.5 };

  // stock the room with free chests + coins (the door already cost the keys)
  const gkey = theme.assets && theme.assets.chest_gold ? theme.assets.chest_gold : null;
  const ckey = theme.assets && theme.assets.coins ? theme.assets.coins : null;
  level.addProp({ gx: vcx - 0.5, gy: vcy + 0.5, shape: "chest_gold", color: 0xc9a24b, blocking: true, breakable: true, table: "chest", scale: 1.1, rot: 0, key: gkey });
  level.addProp({ gx: vcx + 1.5, gy: vcy + 0.5, shape: "chest_gold", color: 0xc9a24b, blocking: true, breakable: true, table: "chest", scale: 1.1, rot: 0, key: gkey });
  level.addProp({ gx: vcx + 0.5, gy: vcy - 0.5, shape: "coins", color: 0xf0c34b, blocking: false, scale: 1.3, rot: 0, key: ckey });
}

export function generateDungeon(floor, theme) {
  const level = new Level(GRID_W, GRID_H, theme);
  const rooms = [];
  const roomCount = BASE_ROOMS + Math.min(floor, 6);
  let attempts = 0;

  while (rooms.length < roomCount && attempts < 260) {
    attempts++;
    const rw = rndi(5, 9), rh = rndi(5, 9);
    const rx = rndi(1, GRID_W - rw - 1), ry = rndi(1, GRID_H - rh - 1);
    let overlap = false;
    for (const r of rooms) {
      if (rx < r.x + r.w + 1 && rx + rw + 1 > r.x &&
          ry < r.y + r.h + 1 && ry + rh + 1 > r.y) { overlap = true; break; }
    }
    if (overlap) continue;
    rooms.push({ x: rx, y: ry, w: rw, h: rh, cx: Math.floor(rx + rw / 2), cy: Math.floor(ry + rh / 2), kind: "room" });
  }

  for (const r of rooms)
    for (let y = r.y; y < r.y + r.h; y++)
      for (let x = r.x; x < r.x + r.w; x++) level.setFloor(x, y, (x + y) % 2);

  const carve = (a, b) => {
    let x = a.cx, y = a.cy;
    while (x !== b.cx) { level.setFloor(x, y); level.setFloor(x, y + 1); x += b.cx > x ? 1 : -1; }
    while (y !== b.cy) { level.setFloor(x, y); level.setFloor(x + 1, y); y += b.cy > y ? 1 : -1; }
    level.setFloor(b.cx, b.cy);
  };
  for (let i = 1; i < rooms.length; i++) carve(rooms[i - 1], rooms[i]);
  for (let k = 0; k < 2 && rooms.length > 3; k++) { const a = pick(rooms), b = pick(rooms); if (a !== b) carve(a, b); }

  level.regions = rooms;
  const start = rooms[0];
  start.kind = "entrance";
  level.playerStart = { gx: start.cx, gy: start.cy };

  // assign archetypes + furnish + spawn candidates
  let ai = Math.floor(Math.random() * ARCHETYPES.length);
  for (const r of rooms) {
    if (r === start) continue;
    r.kind = ARCHETYPES[ai % ARCHETYPES.length]; ai++;
    const { interior } = roomCells(r);
    for (const [x, y] of interior) level.spawnPoints.push({ gx: x + 0.5, gy: y + 0.5 });
    furnishRoom(level, theme, r, [[start.cx, start.cy]]);
  }

  // optional sealed vault room (locked door + hoard), carved before finalize so
  // its unreachable interior never captures the exit or enemy spawns
  carveVault(level, rooms, floor, theme);

  // exit + reachable spawns. Rooms connect freely; only the vault door is gated.
  finalize(level);
  return level;
}
